import React, { ReactNode, useState } from 'react'
import { ThemeProvider, useMediaQuery, useTheme } from '@mui/material'

import { useTranslation } from 'language/translator'

import { TPublicDashboard } from 'types/GlobalDashboard'
import { WidgetPatchBody } from 'types/GlobalWidget'

import { useDashboardFilter, usePeriodFilter } from 'redux/hooks/DashboardPage'

import DashboardUserFilter from 'components_new/organisms/DashboardUserFilter'
import DashboardUserFilterBar from 'components_new/organisms/DashboardUserFilterBar'
import VisuallyHidden from 'components_new/atoms/VisuallyHidden'

import ExportDialog from 'components_new/organisms/dialogs/ExportDialog'

import { getTheme } from 'themes'

import PageLayout, { RightSideBarButtons } from '../PageLayout'

export type InnerWidgetPatchBody = WidgetPatchBody['data']

interface PublicDashboardTemplateProps {
  allowDashboardFilter?: boolean
  allowExport?: boolean
  children: ReactNode
  dashboard: TPublicDashboard
  mobileBreakpoint?: number
  mobileResponsive?: boolean
}

const PublicDashboardTemplate = (props: PublicDashboardTemplateProps) => {
  const {
    allowDashboardFilter,
    allowExport,
    children,
    dashboard,
    mobileBreakpoint,
    mobileResponsive
  } = props
  const [exportDialogOpen, setExportDialogOpen] = useState<boolean>(false)
  const { t } = useTranslation()

  const currentTheme = useTheme()

  const theme = getTheme(currentTheme.palette.mode, null, {
    breakpoint: mobileBreakpoint,
    responsive: mobileResponsive
  })

  const isMobile = useMediaQuery(theme.breakpoints.down('mobile'), {
    noSsr: true
  })

  const dashboardFilter = useDashboardFilter()
  const periodFilter = usePeriodFilter()

  const [rightSidebarOpen, setRightSidebarOpen] = useState<boolean>(
    allowDashboardFilter ?? false
  )

  const rightSidebarButtons: RightSideBarButtons[] = []

  if (allowDashboardFilter) {
    rightSidebarButtons.push({
      onClick: () => setRightSidebarOpen(!rightSidebarOpen),
      icon: { name: 'Filter' },
      title: t('global', 'filter')
    })
  }

  if (allowExport) {
    rightSidebarButtons.push({
      onClick: () => setExportDialogOpen(true),
      icon: { name: 'Download' },
      title: t('global', 'export')
    })
  }

  return (
    <ThemeProvider theme={theme}>
      <PageLayout
        content={{
          disablePadding: true
        }}
        rightSidebar={
          rightSidebarButtons.length > 0 && !isMobile
            ? {
                buttons: rightSidebarButtons,
                collapsed: !rightSidebarOpen,
                content: (
                  <DashboardUserFilter
                    dashboard={dashboard}
                    dashboardGroup={null}
                    dashboardFilters={dashboard.dashboard_filters}
                  />
                ),
                size: 'small'
              }
            : undefined
        }
        toolbar={{
          collapsed: dashboardFilter.length === 0 && !Boolean(periodFilter),
          content: <DashboardUserFilterBar dashboard={dashboard} />
        }}
        variant="static"
      >
        <VisuallyHidden component="h1">{dashboard.title}</VisuallyHidden>
        {children}
        <ExportDialog
          dashboard={dashboard}
          open={exportDialogOpen}
          onClose={() => setExportDialogOpen(false)}
          onOpen={() => setExportDialogOpen(true)}
        />
      </PageLayout>
    </ThemeProvider>
  )
}

export default PublicDashboardTemplate
